import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import numeral from 'numeral';

import SpinnerBlock from '../../common/SpinnerBlock';
import EditOrLock from './common/EditOrLock';

import { showModal } from '../../../actions/modal';

import { getCurrencySymbol } from '../../../helpers';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  height: calc(100% - 24px);
  position: relative;
  padding-left: 0;
  padding-right: 0;
  box-shadow: none;
  border: 1px solid #ebebeb;
  > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 10px;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .payment-flex {
    .payment-flex-block {
      display: flex;
      align-items: center;
      padding-top: 18px;
    }
    .payment-status-button {
      display: flex;
      align-items: center;
      font-weight: 600;
      margin-top: 6px;

      .custom-control-label:before {
        background-color: #dae1e5;
        border-color: #dae1e5;
        cursor: pointer;
      }
      .custom-control-label:after {
        background-color: #fff;
      }
      .custom-control-input:checked ~ .custom-control-label:before {
        color: #7e57c2;
        border-color: #7e57c2;
        background-color: #7e57c2;
      }

      .custom-control-input:checked:disabled ~ .custom-control-label:before {
        color: #dae1e5;
        border-color: #dae1e5;
        background-color: #dae1e5;
      }
    }
  }
  .change-link {
    display: flex;
    align-items: center;
  }
  .payment-badge {
    position: relative;
    margin-left: 10px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    border-radius: 30px;
    background: #ccc;
    border: 1px solid #fff;
    color: #296775;
    padding: 2px 12px 0;
    min-width: 80px;
    height: auto;
    align-items: flex-start;
    .status-badge-tip {
      text-align: left;
    }
    i {
      font-size: 12px;
      margin-top: 1px;
    }
  }
  .payment-badge .status-badge-tip {
    left: 0;
    width: 200px;
    top: 33px;
  }
  .status-badge-wrapper {
    position: relative;
  }
  .status-badge-tip {
    position: absolute;
    z-index: 100;
    background: white;
    padding: 5px 10px;
    border-radius: 3px;
    width: 150px;
    border: 1px solid #ebebeb;
    top: 25px;
    left: 8px;
  }
  .top-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 3px;
    padding-bottom: 23px;
    flex-wrap: wrap;
    h4 {
      margin: 0;
      font-size: 16px;
    }
    .left {
      display: flex;
    }
  }
  @media (max-width: 1500px) {
    .top-block {
      .left {
        margin: 5px 0;
      }
    }
    .payment-badge {
      margin-left: 0;
    }
  }
`;

const PaymentBlock = (props) => {
  const { haveProducts, currentOrder, sum, loadingPayment, PaymentBadge, handleEditPayment } =
    props;

  const dispatch = useDispatch();
  const { paymentMethodName } = currentOrder;
  const { checkoutSettings } = useSelector((state) => state.settings);

  const { t } = useAutoTranslation();

  const handlePaymentModal = () => {
    dispatch(
      showModal('EditOrderPayment', {
        currentOrder,
        submitCallback: handleEditPayment,
      }),
    );
  };

  const handleUpdatePaymentStatus = () => {
    const newStatus = currentOrder.paymentStatus === 'PAID' ? 'NOT_PAID' : 'PAID';
    handleEditPayment({ paymentStatus: newStatus });
  };

  const currencySymbol = currentOrder.currency
    ? getCurrencySymbol(currentOrder.currency)
    : getCurrencySymbol(checkoutSettings.currency);

  const idPaidPermission =
    currentOrder.permissionList &&
    currentOrder.permissionList.fieldPermissions &&
    currentOrder.permissionList.fieldPermissions.find((p) => p.type === 'IS_PAID_EDITABLE');
  const isPaidEditable = idPaidPermission ? idPaidPermission.value : true;

  return (
    <Wrapper className={`card-box ${!haveProducts ? 'disabled' : ''}`}>
      <SpinnerBlock show={loadingPayment} />
      <div style={{ display: 'flex' }} className="card-title top-block">
        <h4 className="header-title">{t('Оплата')}</h4>
        <div className="left">
          {haveProducts ? (
            <div className="mr-2">
              <PaymentBadge {...currentOrder} />
            </div>
          ) : null}
          <EditOrLock
            permissions={currentOrder.permissionList}
            permissionKey="PAYMENT_EDITABLE"
            action={handlePaymentModal}
          />
        </div>
      </div>
      {haveProducts ? (
        <Fragment>
          <div className="border-top" style={{ padding: 0, paddingBottom: '8px' }} />
          <div className="payment-flex">
            <div className="payment-flex-block">
              <div className="block-subtitle">{t('Способ оплаты')}</div>
            </div>
            <div className="payment-flex-block" style={{ paddingTop: '10px' }}>
              <div>{t(paymentMethodName) || ''}</div>
              <div
                style={{
                  marginLeft: 'auto',
                  fontSize: '14px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >{`${numeral(sum).format('0.[00]')} ${currencySymbol}`}</div>
            </div>
            {currentOrder.paymentMethodType === 'NO_COD' ||
            currentOrder.paymentMethodType === 'NO_COD_NO_CONTROL' ? (
              <div className="payment-status-button">
                <div style={{ marginLeft: 'auto' }}>
                  {currentOrder.paymentStatus === 'PAID' ? t('Оплачен') : t('Не оплачен')}
                </div>
                <div
                  className={`custom-control custom-switch ${
                    !isPaidEditable ? 'help-tooltip-wrapper' : ''
                  }`}
                  style={{ marginLeft: '9px', marginRight: '-8px' }}
                >
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    disabled={!isPaidEditable}
                    id={`payment-status-button`}
                    checked={currentOrder.paymentStatus === 'PAID'}
                    onChange={() => handleUpdatePaymentStatus()}
                  />
                  <label className="custom-control-label" htmlFor={`payment-status-button`} />
                  {!isPaidEditable ? (
                    <span className="help-tooltip">{idPaidPermission.reason}</span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="block-subtitle" style={{ marginTop: '16px', paddingTop: '4px' }}>
              {t('Страховка при доставке')}
            </div>
            <div className="payment-flex-block" style={{ paddingTop: '8px' }}>
              <div>{t('Объявленная ценность')}</div>
              <div
                style={{
                  marginLeft: 'auto',
                  fontSize: '14px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >{`${numeral(currentOrder.insuranceSum).format('0.[00]')} ${currencySymbol}`}</div>
            </div>
            {!!currentOrder.expenses?.length && (
              <>
                <div className="block-subtitle" style={{ marginTop: '16px', paddingTop: '4px' }}>
                  {t('Доставка')}
                </div>
                <div className="payment-flex-block" style={{ paddingTop: '8px' }}>
                  <div>{t('Стоимость доставки')}</div>
                  <div
                    style={{
                      marginLeft: 'auto',
                      fontWeight: 700,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {currentOrder.expenses?.reduce(
                      (sum, expense) =>
                        sum + expense.items.find((item) => item.type === `DELIVERY`)?.sum || 0,
                      0,
                    )}{' '}
                    {currencySymbol}
                  </div>
                </div>
              </>
            )}
          </div>
        </Fragment>
      ) : (
        <div className="blocked-block">
          <div className="block-subtitle">{t('Изменения не доступны')}</div>
        </div>
      )}
    </Wrapper>
  );
};

export default PaymentBlock;
